/**
 * Front Desk route tree, mounted by the shell at /desk (working name; code
 * says desk).
 *
 * ProtectedRoute requires a Desk.* role during the pilot (Desk.User; global
 * Admin passes). Which TICKETS a person sees is decided in the BFF store; the
 * team queues in the nav are a convenience built from /api/desk/me and each
 * team's saved queues, and the API refuses a team the caller is not on.
 */

import { lazy, Suspense, createContext, useContext } from 'react';
import { Navigate, Outlet, type RouteObject } from 'react-router-dom';
import { Spinner } from '@fluentui/react-components';
import {
  AddCircleRegular,
  BoardRegular,
  FilterRegular,
  PersonRegular,
  TicketDiagonalRegular,
} from '@fluentui/react-icons';
import { useQueries } from '@tanstack/react-query';
import { AppShell, type NavEntry } from '@widgets/app-shell';
import { ErrorBoundary } from '@shared/ui';
import { ProtectedRoute } from '@shared/auth';
import { fetchTeam } from '@desk/api/desk';
import { deskKeys, useDeskMe } from '@desk/api/useDesk';

/** Daybreak Amber, inherited from Ideas: Front Desk absorbs Ideas later (DESIGN.md 0.5). */
export const DESK_ACCENT = '#F79E31';

const StartPage = lazy(() => import('@desk/pages/start/StartPage').then((m) => ({ default: m.StartPage })));
const NewRequestPage = lazy(() => import('@desk/pages/new/NewRequestPage').then((m) => ({ default: m.NewRequestPage })));
const TicketListPage = lazy(() => import('@desk/pages/list/TicketListPage').then((m) => ({ default: m.TicketListPage })));
const QueuePage = lazy(() => import('@desk/pages/queue/QueuePage').then((m) => ({ default: m.QueuePage })));
const TicketPage = lazy(() => import('@desk/pages/ticket/TicketPage').then((m) => ({ default: m.TicketPage })));

function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<Spinner style={{ padding: '2rem' }} label="Loading..." />}>{children}</Suspense>;
}

const DeskBasePathContext = createContext<string>('');

export function useDeskBasePath(): string {
  return useContext(DeskBasePathContext);
}

function useNavItems(basePath: string): NavEntry[] {
  const me = useDeskMe().data;
  const teams = (me?.teams ?? []).slice().sort((a, b) => a.name.localeCompare(b.name));
  const configs = useQueries({
    queries: teams.map((t) => ({ queryKey: deskKeys.team(t.id), queryFn: () => fetchTeam(t.id), staleTime: 60_000 })),
  });
  const p = (suffix: string) => `${basePath}${suffix}` || '/';

  const entries: NavEntry[] = [
    { path: p('/'), label: 'New request', icon: AddCircleRegular },
    { path: p('/mine'), label: 'My requests', icon: TicketDiagonalRegular },
  ];
  if (teams.length) entries.push({ path: p('/assigned'), label: 'Assigned to me', icon: PersonRegular });
  teams.forEach((t, i) => {
    const cfg = configs[i]?.data;
    entries.push({ kind: 'section', label: t.name });
    // Queue order is the team's own (sort column), a deliberate choice by the team.
    for (const qd of cfg?.queues ?? []) {
      entries.push({
        path: p(`/team/${t.id}/q/${qd.id}`),
        label: qd.name,
        icon: qd.view === 'board' ? BoardRegular : FilterRegular,
      });
    }
  });
  return entries;
}

export function DeskRootLayout() {
  const basePath = useDeskBasePath();
  const navItems = useNavItems(basePath);
  return (
    <ProtectedRoute appRolePrefix="Desk">
      <ErrorBoundary>
        <AppShell appId="desk" appTitle="Front Desk" headerColor={DESK_ACCENT} navItems={navItems}>
          <Outlet />
        </AppShell>
      </ErrorBoundary>
    </ProtectedRoute>
  );
}

export function deskRoutes(basePath: string = ''): RouteObject[] {
  return [
    {
      element: (
        <DeskBasePathContext.Provider value={basePath}>
          <DeskRootLayout />
        </DeskBasePathContext.Provider>
      ),
      children: [
        { index: true, element: <LazyPage><StartPage /></LazyPage> },
        { path: 'new/:typeId', element: <LazyPage><NewRequestPage /></LazyPage> },
        { path: 'mine', element: <LazyPage><TicketListPage scope="mine" /></LazyPage> },
        { path: 'assigned', element: <LazyPage><TicketListPage scope="assigned" /></LazyPage> },
        { path: 'team/:teamId/q/:queueId', element: <LazyPage><QueuePage /></LazyPage> },
        { path: 't/:number', element: <LazyPage><TicketPage /></LazyPage> },
        { path: '*', element: <Navigate to={basePath || '/'} replace /> },
      ],
    },
  ];
}
